import React from 'react';
import cn from 'clsx';

import Button from '../../../../library/button/button';
import AddProductButton from '../../../../components/add-product-button/add-product-button';
import MinimumPriceMessage from '../../../../components/minimum-price-message/minimum-price-message';
import ProductArticleStatusBanner from '../../../../components/product-article-status-banner/product-article-status-banner.one-dt';
import ProductAvailability from '../../../../components/product-availability/product-availability';
import ProductFirstPriorityDetails from '../../../../components/product-first-priority-details/product-first-priority-details';
import ProductQuantity from '../../../../components/product-quantity/product-quantity';
import QuantityDisclaimer from '../../../../components/quantity-disclaimer/quantity-disclaimer';
import ProductListCardPrice from '../product-list-card-price/product-list-card-price';
import ProductListCardPromos from '../product-list-card-promos/product-list-card-promos';
import ProductListReviewDetails from '../product-list-review-details/product-list-review-details';

import { useProductEntryDataLayerValues } from '../../../../shared/hooks/hooks';

import { trackAction } from '../../../../shared/utils/analytics/analytics';
import {
  getNormalizedProductType,
  getPrioritizedArticleStatusProduct,
  getShouldShowMinimumPriceMessage,
  getShouldShowProductArticleStatusBanner,
  isCanonicalProduct
} from '../../../../shared/utils/product/product';
import { setSelectedProductAvailabilityStore } from '../../utils/product-list-page-multistore-utils';

import { ProductData } from '../../../../shared/types/graphql-types';
import { BundleData, ProductItem } from '../../../../shared/types/product';

import styles from './product-list-card-actions.scss';

interface ProductListCardActionsProps {
  defaultQuantity: number;
  hideFoundItLower?: boolean;
  hideProductPrice?: boolean;
  isTreadwellTabSelected?: boolean;
  onAddProductClick: (productItem: ProductItem) => void;
  onCheckInventoryClick?: () => void;
  onQuantityChange: (value: number) => void;
  onViewProductClick?: () => void;
  prebuiltPackage?: BundleData;
  product: ProductData;
  productDetailUrl?: string;
  quantity: number;
  showAvailability?: boolean;
  showFirstPriorityDetails?: boolean;
  showReportCard?: boolean;
  totalQuantity: number;
}

function ProductListCardActions(props: ProductListCardActionsProps) {
  const {
    defaultQuantity,
    hideFoundItLower = false,
    hideProductPrice = false,
    isTreadwellTabSelected = false,
    onAddProductClick,
    onCheckInventoryClick,
    onQuantityChange,
    onViewProductClick,
    prebuiltPackage,
    product,
    productDetailUrl = '',
    quantity,
    showAvailability = false,
    showFirstPriorityDetails = false,
    showReportCard = false,
    totalQuantity
  } = props;

  const { mapDisplayRule } = product || {};
  const articleStatusPreBuiltProduct = getPrioritizedArticleStatusProduct(
    prebuiltPackage?.product1,
    prebuiltPackage?.product2
  );
  const articleStatusProduct = getPrioritizedArticleStatusProduct(
    product,
    product?.rearProduct
  );
  const shouldShowProductArticleStatusBanner =
    getShouldShowProductArticleStatusBanner(articleStatusProduct);

  let addToCartDisabled;
  if (prebuiltPackage) {
    addToCartDisabled = articleStatusPreBuiltProduct.addToCartDisabled;
  } else {
    addToCartDisabled = product?.addToCartDisabled;
  }

  const isCanonical = isCanonicalProduct(product);
  const productType = getNormalizedProductType(product);

  const showAddToCart = !addToCartDisabled;

  const showMinimumPriceMessage = getShouldShowMinimumPriceMessage(product);

  const { getProductEntryDataLayerValues } = useProductEntryDataLayerValues();

  function handleQuantityChange(e, updatedValue) {
    onQuantityChange(updatedValue);
  }

  if (!isCanonical && !showAddToCart && !showAvailability) return null;

  return (
    <div className={cn(styles['container'], 'product-list-card-actions')}>
      {!isCanonical && !showAddToCart && showAvailability && product ? (
        <>
          {shouldShowProductArticleStatusBanner && (
            <ProductArticleStatusBanner
              product={product}
              showBoxDesign={process.env.__FF_PLP_CONDENSED_CARDS__ === 'true'}
            />
          )}
          {!shouldShowProductArticleStatusBanner && (
            <ProductAvailability
              onCheckInventoryClick={onCheckInventoryClick}
              product={product}
              quantity={quantity}
              showBoxDesign={process.env.__FF_PLP_CONDENSED_CARDS__ === 'true'}
            />
          )}
        </>
      ) : (
        <>
          <div className={styles['price-info']}>
            {!hideProductPrice && (
              <ProductListCardPrice
                hideFoundItLower={hideFoundItLower}
                hidePercentageSaved
                product={product}
                quantity={quantity}
                showStaticQuantity={Boolean(prebuiltPackage)}
                totalQuantity={totalQuantity}
              />
            )}

            {showAvailability && product && (
              <>
                {shouldShowProductArticleStatusBanner ? (
                  <ProductArticleStatusBanner
                    product={product}
                    showBoxDesign={
                      process.env.__FF_PLP_CONDENSED_CARDS__ === 'true'
                    }
                  />
                ) : (
                  <ProductAvailability
                    onCheckInventoryClick={onCheckInventoryClick}
                    product={product}
                    quantity={quantity}
                    showBoxDesign={
                      process.env.__FF_PLP_CONDENSED_CARDS__ === 'true'
                    }
                  />
                )}
              </>
            )}

            {showReportCard &&
              (showFirstPriorityDetails ? (
                <ProductFirstPriorityDetails
                  dataReportUrl={productDetailUrl}
                  isCollapsible={
                    process.env.__FF_PLP_CONDENSED_CARDS__ === 'true'
                  }
                  onClick={() => {
                    trackAction(
                      'product_view_pdl_data_click',
                      getProductEntryDataLayerValues(product)
                    );
                    setSelectedProductAvailabilityStore(product);
                  }}
                  product={product}
                />
              ) : (
                <ProductListReviewDetails
                  isTreadwellTabSelected={isTreadwellTabSelected}
                  numberOfReviews={product?.reviewSummaryData?.totalCount || 0}
                  onClickReadReviewsFn={() =>
                    setSelectedProductAvailabilityStore(product)
                  }
                  product={product}
                  productDetailUrl={productDetailUrl}
                />
              ))}

            {showMinimumPriceMessage && (
              <MinimumPriceMessage message={mapDisplayRule?.message} />
            )}

            <ProductListCardPromos
              product={prebuiltPackage ? prebuiltPackage.product1 : product}
            />
          </div>

          {!prebuiltPackage && (
            <QuantityDisclaimer productType={productType} quantity={quantity} />
          )}

          {isCanonical ? (
            <Button
              className={styles['main-cta']}
              href={productDetailUrl}
              isFullWidth
              isPrimary
              onClick={onViewProductClick}
            >
              View Details
            </Button>
          ) : (
            showAddToCart && (
              <div className={styles['add-to-cart']}>
                <ProductQuantity
                  defaultValue={defaultQuantity}
                  id="quantity"
                  onBlur={handleQuantityChange}
                  onChange={handleQuantityChange}
                  value={quantity}
                />

                <AddProductButton
                  className={styles['main-cta']}
                  onClick={onAddProductClick}
                  productItem={{
                    prebuiltPackage,
                    product,
                    quantity,
                    totalQuantity
                  }}
                />
              </div>
            )
          )}
        </>
      )}
    </div>
  );
}

export default ProductListCardActions;
